import React, { useCallback, useEffect, useState } from "react";
import Login from "./Login";
import AdminRollers from "../components/admin/AdminRollers";
import AdminTrainers from "../components/admin/AdminTrainers";
import {
  AdminRoles,
  createTrainer,
  getAdminContext,
  listLevels,
  listRollers,
  listTrainers,
  logout,
  onAdminAuthStateChanged,
  updateRollerAdmin,
  updateTrainer,
} from "../models/firebase/admin";
import "./Admin.css";

function useNoIndex() {
  useEffect(() => {
    const previousTitle = document.title;
    const existingMeta = document.querySelector('meta[name="robots"]');
    const previousContent = existingMeta
      ? existingMeta.getAttribute("content")
      : null;
    const meta = existingMeta || document.createElement("meta");

    document.title = "Broller Admin";
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex,nofollow");

    if (!existingMeta) {
      document.head.appendChild(meta);
    }

    return () => {
      document.title = previousTitle;

      if (existingMeta && previousContent !== null) {
        existingMeta.setAttribute("content", previousContent);
      } else if (!existingMeta && meta.parentNode) {
        meta.parentNode.removeChild(meta);
      }
    };
  }, []);
}

function Admin() {
  useNoIndex();

  const [status, setStatus] = useState("loading");
  const [context, setContext] = useState(null);
  const [rollers, setRollers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [activeTab, setActiveTab] = useState("rollers");
  const [error, setError] = useState(null);

  const isSuperadmin =
    context && context.adminUser.role === AdminRoles.superadmin;

  const loadAdminData = useCallback(async (adminContext) => {
    const adminUser = adminContext.adminUser;
    const rollersData = await listRollers(adminUser);
    const levelsData = await listLevels();
    const trainersData =
      adminUser.role === AdminRoles.superadmin
        ? await listTrainers()
        : adminContext.trainer
        ? [adminContext.trainer]
        : [];

    setContext(adminContext);
    setRollers(rollersData);
    setLevels(levelsData);
    setTrainers(trainersData);
    setStatus("ready");
  }, []);

  const loadAdminContext = useCallback(
    async (firebaseUser) => {
      setStatus("loading");
      setError(null);

      try {
        const adminContext = await getAdminContext(firebaseUser);

        if (!adminContext) {
          setContext(null);
          setRollers([]);
          setTrainers([]);
          setStatus("denied");
          return;
        }

        await loadAdminData(adminContext);
      } catch (e) {
        setError(e.message || "Не вдалося завантажити адмінку.");
        setStatus("error");
      }
    },
    [loadAdminData]
  );

  const refreshData = useCallback(async () => {
    if (context) {
      await loadAdminData(context);
    }
  }, [context, loadAdminData]);

  useEffect(() => {
    return onAdminAuthStateChanged((firebaseUser) => {
      if (!firebaseUser) {
        setContext(null);
        setRollers([]);
        setTrainers([]);
        setStatus("login");
        return;
      }

      loadAdminContext(firebaseUser);
    });
  }, [loadAdminContext]);

  const onAdminLogin = useCallback(
    async (authResult) => {
      if (authResult && authResult.user) {
        await loadAdminContext(authResult.user);
      }
    },
    [loadAdminContext]
  );

  const onLogout = useCallback(async () => {
    await logout();
    setStatus("login");
  }, []);

  const saveRoller = useCallback(
    async (id, data) => {
      setError(null);

      try {
        await updateRollerAdmin(id, data);
        await refreshData();
      } catch (e) {
        setError(e.message || "Не вдалося зберегти роллера.");
        throw e;
      }
    },
    [refreshData]
  );

  const saveNewTrainer = useCallback(
    async (data) => {
      setError(null);

      try {
        await createTrainer(data);
        await refreshData();
      } catch (e) {
        setError(e.message || "Не вдалося створити тренера.");
        throw e;
      }
    },
    [refreshData]
  );

  const saveTrainer = useCallback(
    async (id, data) => {
      setError(null);

      try {
        await updateTrainer(id, data);
        await refreshData();
      } catch (e) {
        setError(e.message || "Не вдалося зберегти тренера.");
        throw e;
      }
    },
    [refreshData]
  );

  if (status === "loading") {
    return <div className="admin-panel__status">Завантаження...</div>;
  }

  if (status === "login") {
    return (
      <div className="admin-panel">
        <div className="admin-panel__header">
          <h1>Адмінка Broller</h1>
        </div>
        <Login onLogin={onAdminLogin} />
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="admin-panel admin-panel__access">
        <h1>Доступ заборонено</h1>
        <p>Цей номер телефону не має доступу до адмінки.</p>
        <button type="button" className="btn btn-primary" onClick={onLogout}>
          Вийти
        </button>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="admin-panel admin-panel__access">
        <h1>Помилка</h1>
        <p>{error}</p>
        <button type="button" className="btn btn-primary" onClick={onLogout}>
          Вийти
        </button>
      </div>
    );
  }

  return (
    <div className="admin-panel container mt-3">
      <div className="admin-panel__top">
        <div>
          <h1 className="h4 mb-1">Адмінка Broller</h1>
          <div className="text-muted small">
            {isSuperadmin ? "Суперадмін" : "Тренер"}
            {context && context.trainer ? `: ${context.trainer.name}` : ""}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onLogout}
        >
          Вийти
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {isSuperadmin ? (
        <div className="admin-panel__tabs btn-group mb-3" role="group">
          <button
            type="button"
            className={
              "btn btn-sm " +
              (activeTab === "rollers" ? "btn-primary" : "btn-outline-primary")
            }
            onClick={() => setActiveTab("rollers")}
          >
            Роллери
          </button>
          <button
            type="button"
            className={
              "btn btn-sm " +
              (activeTab === "trainers"
                ? "btn-primary"
                : "btn-outline-primary")
            }
            onClick={() => setActiveTab("trainers")}
          >
            Тренери
          </button>
        </div>
      ) : null}

      {activeTab === "trainers" && isSuperadmin ? (
        <AdminTrainers
          trainers={trainers}
          onCreate={saveNewTrainer}
          onUpdate={saveTrainer}
        />
      ) : (
        <AdminRollers
          rollers={rollers}
          trainers={trainers}
          levels={levels}
          canEdit={isSuperadmin}
          canAssign={isSuperadmin}
          onSave={saveRoller}
        />
      )}
    </div>
  );
}

export default Admin;
